import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable, of } from 'rxjs';
import { catchError, map, tap } from 'rxjs/operators';
import { Comment } from '../../interfaces/comment.interface';
import { IUserAuthenticated } from '@data/interfaces/iuser-authenticated.interface';
import { ApiLeaseService } from '../api/api-lease.service';

@Injectable({
  providedIn: 'root'
})
export class CommentsService {
  // Estado reactivo para comentarios por lease
  private commentsSubject = new BehaviorSubject<Comment[]>([]);
  public comments$ = this.commentsSubject.asObservable();

  // Estado de carga
  private loadingSubject = new BehaviorSubject<boolean>(false);
  public loading$ = this.loadingSubject.asObservable();

  constructor(
    private apiLeaseService: ApiLeaseService
  ) {}

  /**
   * Cargar comentarios de un lease específico
   */
  loadComments(leaseId: string): Observable<Comment[]> {
    console.log('🔍 CommentsService.loadComments() llamado para lease:', leaseId);
    this.loadingSubject.next(true);
    
    return this.apiLeaseService.getComments(leaseId).pipe(
      map((response: any) => {
        console.log('📨 Respuesta de getComments:', response);
        if (!response.error && Array.isArray(response.data)) {
          console.log('✅ Comentarios cargados exitosamente:', response.data.length, 'comentarios');
          this.commentsSubject.next(response.data);
          return response.data;
        } else {
          console.error('❌ Error al cargar comentarios:', response.msg);
          this.commentsSubject.next([]);
          return [];
        }
      }),
      catchError((error: any) => {
        console.error('Error de conexión al cargar comentarios:', error);
        this.commentsSubject.next([]);
        return of([]);
      }),
      tap(() => this.loadingSubject.next(false))
    );
  }

  /**
   * Crear un nuevo comentario
   */
  createComment(leaseId: string, content: string): Observable<{ success: boolean, message: string }> {
    // Validaciones
    const validation = this.validateComment(content);
    if (!validation.isValid) {
      return of({ success: false, message: validation.errorMessage });
    }

    this.loadingSubject.next(true);

    return this.apiLeaseService.createComment(leaseId, content.trim()).pipe(
      map((response: any) => {
        if (!response.error && response.data && response.data.comments) {
          // Actualizar el estado local con los comentarios del lease actualizado
          this.commentsSubject.next(response.data.comments);
          return { success: true, message: 'Comentario agregado correctamente' };
        } else {
          return { success: false, message: response.msg || 'Error al crear comentario' };
        }
      }),
      catchError((error: any) => {
        console.error('Error al crear comentario:', error);
        return of({ success: false, message: 'Error de conexión al crear comentario' });
      }),
      tap(() => this.loadingSubject.next(false))
    );
  }

  /**
   * Eliminar un comentario
   */
  deleteComment(leaseId: string, commentId: string, currentUserId: string): Observable<{ success: boolean, message: string }> {
    // Verificar que el comentario existe
    const comment = this.commentsSubject.value.find(c => c._id === commentId);
    if (!comment) {
      return of({ success: false, message: 'Comentario no encontrado' });
    }

    this.loadingSubject.next(true);

    return this.apiLeaseService.deleteComment(leaseId, commentId).pipe(
      map((response: any) => {
        if (!response.error && response.data && response.data.comments) {
          // Actualizar el estado local con los comentarios del lease actualizado
          this.commentsSubject.next(response.data.comments);
          return { success: true, message: 'Comentario eliminado correctamente' };
        } else {
          return { success: false, message: response.msg || 'Error al eliminar comentario' };
        }
      }),
      catchError((error: any) => {
        console.error('Error al eliminar comentario:', error);
        return of({ success: false, message: 'Error de conexión al eliminar comentario' });
      }),
      tap(() => this.loadingSubject.next(false))
    );
  }

  /**
   * Validar contenido del comentario
   */
  private validateComment(content: string): { isValid: boolean, errorMessage: string } {
    if (!content || !content.trim()) {
      return { isValid: false, errorMessage: 'El comentario no puede estar vacío' };
    }

    if (content.trim().length > 500) {
      return { isValid: false, errorMessage: 'El comentario no puede exceder 500 caracteres' };
    }

    return { isValid: true, errorMessage: '' };
  }

  /**
   * Verificar si el usuario actual puede eliminar un comentario
   */
  canDeleteComment(comment: Comment, currentUserId: string): boolean {
    if (typeof comment.author === 'object' && comment.author._id) {
      return comment.author._id === currentUserId;
    } else if (typeof comment.author === 'string') {
      return comment.author === currentUserId;
    }
    return false;
  }

  /**
   * Obtener el nombre completo del autor de un comentario
   */
  getAuthorDisplayName(comment: Comment): string {
    if (typeof comment.author === 'object' && comment.author.names && comment.author.lastNames) {
      return `${comment.author.names} ${comment.author.lastNames}`;
    } else if (typeof comment.author === 'string') {
      return 'Usuario';
    }
    return 'Autor desconocido';
  }

  /**
   * Formatear fecha de comentario
   */
  formatCommentDate(dateString: string): string {
    try {
      return new Date(dateString).toLocaleString('es-MX', {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      });
    } catch (error) {
      return 'Fecha inválida';
    }
  }

  /**
   * Limpiar estado del servicio
   */
  clearState(): void {
    this.commentsSubject.next([]);
    this.loadingSubject.next(false);
  }

  /**
   * Setear comentarios directamente (para cuando ya vienen en la data del lease)
   */
  setComments(comments: Comment[]): void {
    console.log('📝 CommentsService.setComments() - Seteando', comments.length, 'comentarios');
    this.commentsSubject.next(comments || []);
  }

  /**
   * Obtener comentarios actuales sin suscripción
   */
  getCurrentComments(): Comment[] {
    return this.commentsSubject.value;
  }

  /**
   * Verificar si hay comentarios
   */
  hasComments(): boolean {
    return this.commentsSubject.value.length > 0;
  }
}